<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { PORTRAIT_ASPECT_RATIOS } from '@/shared/lib/portrait-orientation';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import { ImageGrid } from '@/ui-kit';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import LegacyPrintQueueCarousel, {
  type LegacyPrintQueueCarouselApi,
} from './LegacyPrintQueueCarousel.vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import PrintQueueItemStatus from './PrintQueueItemStatus.vue';
import type { PrintingQueueItem } from './usePrintingFlow';

interface GalleryInstance {
  scrollToItem: (uuid: string) => void;
}

export interface PrintQueueGalleryApi {
  scrollToItem: (uuid: string) => void;
  scrollToNextPrintedItemIfNeeded: () => void;
}

const PORTRAITS_PER_LANDSCAPE_SLIDE = 3;
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const PRINT_GRID_LAYOUT = { columns: 1, rows: 1 };

const props = defineProps<{
  errorSettled: boolean;
  hasErrors: boolean;
  isLandscapeKiosk: boolean;
  isVerticalKiosk: boolean;
  items: PrintingQueueItem[];
  useImageGrid: boolean;
}>();

const legacyGallery =
  useTemplateRef<LegacyPrintQueueCarouselApi>('legacyGallery');
const gridGallery = useTemplateRef<GalleryInstance>('gridGallery');
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const gridCellAspect = computed(
  () =>
    props.items.find((portrait) => portrait.aspectRatio)?.aspectRatio ??
    PORTRAIT_ASPECT_RATIOS.horizontal
);
const itemsPerSlide = computed(() =>
  props.isVerticalKiosk ? 1 : PORTRAITS_PER_LANDSCAPE_SLIDE
);

function scrollToItem(uuid: string): void {
  if (props.useImageGrid) {
    gridGallery.value?.scrollToItem(uuid);
    return;
  }
  legacyGallery.value?.scrollToItem(uuid);
}

function scrollToNextPrintedItemIfNeeded(): void {
  const printedPortraits = props.items.filter((portrait) => portrait.isPrinted);
  if (
    printedPortraits.length === 0 ||
    (printedPortraits.length - 1) % itemsPerSlide.value !== 0
  ) {
    return;
  }

  const uuid = printedPortraits.at(-1)?.uuid;
  if (uuid) {
    scrollToItem(uuid);
  }
}

defineExpose<PrintQueueGalleryApi>({
  scrollToItem,
  scrollToNextPrintedItemIfNeeded,
});
</script>

<template>
  <LegacyPrintQueueCarousel
    v-if="!useImageGrid"
    ref="legacyGallery"
    :error-settled="errorSettled"
    :is-landscape-kiosk="isLandscapeKiosk"
    :is-vertical-kiosk="isVerticalKiosk"
    :items="items"
    :items-per-slide="PORTRAITS_PER_LANDSCAPE_SLIDE"
  />

  <div
    v-else
    class="Printing__gallery PrintQueueGallery PrintQueueGallery--grid"
    :class="{
      'PrintQueueGallery--concealed': !hasErrors,
      'PrintQueueGallery--vertical': isVerticalKiosk,
      'PrintQueueGallery--wide': useImageGrid,
    }"
  >
    <ImageGrid
      ref="gridGallery"
      :items="items"
      :layout="PRINT_GRID_LAYOUT"
      sizing="width-fixed"
      cell-width="calc(100vw - var(--Kiosk--wrapper-side-gap) - var(--Kiosk--wrapper-side-gap))"
      :cell-aspect-ratio="gridCellAspect"
      cell-radius="var(--print-queue-item-radius)"
      gap="2.7rem"
      fit="contain"
      state-class-prefix="Printing"
      :show-arrows="false"
    >
      <template #default="{ item: portrait }">
        <PrintQueueItemStatus
          :center-error="true"
          :is-landscape-kiosk="isLandscapeKiosk"
          :is-vertical-kiosk="isVerticalKiosk"
          :portrait="portrait"
        />
      </template>
    </ImageGrid>
  </div>
</template>

<style scoped lang="scss">
.PrintQueueGallery--legacy.ImageGallery {
  --ImageGallery--item-height: 40rem;
}

.PrintQueueGallery--legacy.PrintQueueGallery--error {
  --ImageGallery--item-height: 32rem;
}

.PrintQueueGallery--legacy :deep(.ImageGallery__scrollable) {
  display: flex;
}

.PrintQueueGallery--legacy.PrintQueueGallery--error.PrintQueueGallery--vertical.PrintQueueGallery--single-item
  :deep(.ImageGallery__scrollable) {
  width: 100%;
}

.PrintQueueGallery--legacy :deep(.ImageGallery__list) {
  width: max-content;
}

.PrintQueueGallery--legacy :deep(.ImageGallery__item-wrapper) {
  width: calc(
    var(--ImageGallery--item-height) * var(--ImageGallery--item-ratio)
  );
  height: var(--ImageGallery--item-height);
  display: flex;
  align-items: center;
  justify-content: center;
}

.PrintQueueGallery--legacy :deep(.ImageGallery__item-image) {
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
}

.Printing__gallery {
  width: min(100%, 96rem);
  height: 42rem;
  min-width: 0;
  min-height: 0;
}

.Printing__gallery.PrintQueueGallery--vertical {
  height: 34rem;
}

.Printing__gallery.PrintQueueGallery--wide {
  width: 100%;
  height: 24rem;
}

.PrintQueueGallery--grid :deep(.ImageGrid__item-wrapper),
.PrintQueueGallery--grid :deep(.ImageGrid__item-image) {
  border-radius: var(--print-queue-item-radius);
}

.PrintQueueGallery--grid :deep(.ImageGrid__item-wrapper) {
  overflow: hidden;
}

.PrintQueueGallery--concealed :deep(.ImageGrid__item-wrapper) {
  background: transparent;
  box-shadow: none;
}

.PrintQueueGallery--concealed :deep(.ImageGrid__item-image) {
  opacity: 0;
}
</style>
